/**
 * TanStack Query hooks for the company catalog + cert lifecycle.
 *
 * Query-key contract (always scoped under the active team — TeamProvider's
 * activeTeamId is part of the key so a team-switch automatically refetches):
 *   ['companies', teamId]                                — list
 *   ['companies', teamId, companyId]                     — detail
 *   ['companies', teamId, companyId, 'dian-processes']   — recent DIAN processes
 *
 * Mutations invalidate the keys most likely to be displayed after the action,
 * so a successful cert upload refreshes the company detail without an
 * explicit refetch in the form.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import {
  createCompany,
  deleteCert,
  deleteCompany,
  getCompany,
  getCompanyLogoBlob,
  listCompanies,
  listDianProcesses,
  removeCompanyLogo,
  updateCompany,
  uploadCompanyLogo,
  type CompanyDetail,
  type CompanyLogoDeleted,
  type CompanyLogoUpdated,
  type CompanySummary,
  type CreateCompanyInput,
  type ListCompaniesResponse,
  type ListDianProcessesResponse,
  type ListStatusFilter,
  type UpdateCompanyInput,
  type UploadCompanyLogoInput,
} from "@/lib/api/companies";
import { useActiveTeam } from "@/lib/team-context";

export const companiesKeys = {
  all: (teamId: string | null) => ["companies", teamId ?? "__no_team__"] as const,
  list: (teamId: string | null, status: ListStatusFilter = "active") =>
    [...companiesKeys.all(teamId), "list", status] as const,
  detail: (teamId: string | null, companyId: string) =>
    [...companiesKeys.all(teamId), companyId] as const,
  dianProcesses: (teamId: string | null, companyId: string) =>
    [...companiesKeys.detail(teamId, companyId), "dian-processes"] as const,
  logo: (teamId: string | null, companyId: string) =>
    [...companiesKeys.detail(teamId, companyId), "logo"] as const,
};

// ── Reads ─────────────────────────────────────────────────────────────────

export function useCompanies(
  options: { status?: ListStatusFilter } = {},
) {
  const { activeTeamId, ready } = useActiveTeam();
  const status = options.status ?? "active";
  return useQuery<ListCompaniesResponse>({
    queryKey: companiesKeys.list(activeTeamId, status),
    queryFn: () => listCompanies({ status }),
    enabled: ready && !!activeTeamId,
  });
}

export function useCompany(
  companyId: string | undefined,
  options: Pick<UseQueryOptions<CompanyDetail>, "refetchInterval"> = {},
) {
  const { activeTeamId } = useActiveTeam();
  return useQuery<CompanyDetail>({
    queryKey: companyId
      ? companiesKeys.detail(activeTeamId, companyId)
      : ["companies", "__no_team__", "__missing__"],
    queryFn: () => getCompany(companyId!),
    enabled: !!companyId && !!activeTeamId,
    refetchInterval: options.refetchInterval,
  });
}

export function useDianProcesses(companyId: string | undefined) {
  const { activeTeamId } = useActiveTeam();
  return useQuery<ListDianProcessesResponse>({
    queryKey: companyId
      ? companiesKeys.dianProcesses(activeTeamId, companyId)
      : ["companies", "__no_team__", "__missing__", "dian-processes"],
    queryFn: () => listDianProcesses(companyId!),
    enabled: !!companyId && !!activeTeamId,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────

export function useCreateCompany() {
  const qc = useQueryClient();
  const { activeTeamId } = useActiveTeam();
  return useMutation<CompanySummary, Error, CreateCompanyInput>({
    mutationFn: (input) => createCompany(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companiesKeys.all(activeTeamId) });
    },
  });
}

export function useUpdateCompany(companyId: string | undefined) {
  const qc = useQueryClient();
  const { activeTeamId } = useActiveTeam();
  return useMutation<CompanySummary, Error, UpdateCompanyInput>({
    mutationFn: (input) => {
      if (!companyId) throw new Error("companyId required");
      return updateCompany(companyId, input);
    },
    onSuccess: () => {
      if (!companyId) return;
      void qc.invalidateQueries({ queryKey: companiesKeys.all(activeTeamId) });
      void qc.invalidateQueries({
        queryKey: companiesKeys.detail(activeTeamId, companyId),
      });
    },
  });
}

export function useDeleteCompany() {
  const qc = useQueryClient();
  const { activeTeamId } = useActiveTeam();
  return useMutation({
    mutationFn: (companyId: string) => deleteCompany(companyId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companiesKeys.all(activeTeamId) });
    },
  });
}

export function useDeleteCert(companyId: string | undefined) {
  const qc = useQueryClient();
  const { activeTeamId } = useActiveTeam();
  return useMutation({
    mutationFn: () => {
      if (!companyId) throw new Error("companyId required");
      return deleteCert(companyId);
    },
    onSuccess: () => {
      if (!companyId) return;
      void qc.invalidateQueries({
        queryKey: companiesKeys.detail(activeTeamId, companyId),
      });
      void qc.invalidateQueries({ queryKey: companiesKeys.all(activeTeamId) });
    },
  });
}

// ── Branding (per-company logo, PF-335) ───────────────────────────────────

/**
 * Fetches the logo bytes as a Blob (the GET is Bearer-authed, so an <img src>
 * can't hit it directly). The component turns the Blob into an object URL.
 * `enabled` only when the detail says hasLogo — a 404 is not an error state.
 */
export function useCompanyLogo(companyId: string | undefined, hasLogo: boolean) {
  const { activeTeamId } = useActiveTeam();
  return useQuery<Blob>({
    queryKey: companyId
      ? companiesKeys.logo(activeTeamId, companyId)
      : ["companies", "__no_team__", "__missing__", "logo"],
    queryFn: () => getCompanyLogoBlob(companyId!),
    enabled: !!companyId && !!activeTeamId && hasLogo,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUploadCompanyLogo(companyId: string | undefined) {
  const qc = useQueryClient();
  const { activeTeamId } = useActiveTeam();
  return useMutation<CompanyLogoUpdated, Error, UploadCompanyLogoInput>({
    mutationFn: (input) => {
      if (!companyId) throw new Error("companyId required");
      return uploadCompanyLogo(companyId, input);
    },
    onSuccess: () => {
      if (!companyId) return;
      void qc.invalidateQueries({
        queryKey: companiesKeys.detail(activeTeamId, companyId),
      });
    },
  });
}

export function useRemoveCompanyLogo(companyId: string | undefined) {
  const qc = useQueryClient();
  const { activeTeamId } = useActiveTeam();
  return useMutation<CompanyLogoDeleted, Error, void>({
    mutationFn: () => {
      if (!companyId) throw new Error("companyId required");
      return removeCompanyLogo(companyId);
    },
    onSuccess: () => {
      if (!companyId) return;
      void qc.invalidateQueries({
        queryKey: companiesKeys.detail(activeTeamId, companyId),
      });
    },
  });
}
